import { Button, HStack, Spinner, Stack, Text } from "@chakra-ui/react";
import * as React from "react";

import { PatreonBenefit } from "../api/patreon";
import { useWorkspaceLink } from "../hooks";
import { Checkbox } from "./chakra/checkbox";
import {
  DialogActionTrigger,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "./chakra/dialog";
import { Link } from "./link";

interface BenefitsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  benefits: PatreonBenefit[];
  selectedBenefits: string[];
  setSelectedBenefits: (benefits: string[]) => void;
  isLoading: boolean;
  fileName: string;
}

export const BenefitsModal = ({
  isOpen,
  onClose,
  onSave,
  benefits,
  selectedBenefits,
  setSelectedBenefits,
  isLoading,
  fileName,
}: BenefitsModalProps) => {
  const handleToggleBenefit = (benefitId: string) => {
    if (selectedBenefits.includes(benefitId)) {
      setSelectedBenefits(selectedBenefits.filter((id) => id !== benefitId));
    } else {
      setSelectedBenefits([...selectedBenefits, benefitId]);
    }
  };

  return (
    <DialogRoot
      size="md"
      open={isOpen}
      onOpenChange={(e) => !e.open && onClose()}
    >
      <DialogBackdrop />
      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>Manage Benefits for {fileName}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isLoading ? (
            <Spinner />
          ) : benefits.length === 0 ? (
            <NoBenefits />
          ) : (
            <Stack gap="4">
              <Text>
                Users with the following benefits will be able to download this
                file:
              </Text>
              {benefits.map((benefit) => (
                <HStack key={benefit.id} gap="3">
                  <Checkbox
                    checked={selectedBenefits.includes(benefit.id)}
                    onChange={() => handleToggleBenefit(benefit.id)}
                  >
                    {benefit.title}
                  </Checkbox>
                </HStack>
              ))}
            </Stack>
          )}
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button colorPalette="gray" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </DialogActionTrigger>
          <Button colorPalette="primary" onClick={onSave}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

const NoBenefits = () => {
  const workspaceLink = useWorkspaceLink();
  return (
    <Stack gap="4">
      <Text>No benefits available for this workspace.</Text>
      <Text>
        To manage benefits, you need to{" "}
        <Link
          to={workspaceLink("/settings/integrations")}
          variant="underline"
          colorPalette="primary"
        >
          connect your Patreon integration
        </Link>
        .
      </Text>
    </Stack>
  );
};
